import type { GeneratedLineListEntry } from "../../../domain/inspection-book/line-list.js";
import { measureTextWidth, measureLongestTokenWidth } from "./text-metrics.js";

/**
 * AUTOAJUSTE INTELIGENTE DA LISTA DE LINHAS (comando "autoajuste
 * inteligente") — calcula a largura de cada coluna EM TEMPO REAL, a partir
 * das colunas realmente ativas e dos dados reais das Linhas, em vez de uma
 * tabela de larguras fixas mantida à mão. Se uma coluna nova for adicionada
 * a `LINE_LIST_COLUMNS`, este algoritmo já a acomoda sem qualquer edição
 * manual de CSS/PDF (comando, item 18).
 *
 * ALGORITMO (comando, item 4):
 *  1–2. largura mínima de cada coluna = largura real (Roboto, medida em
 *       `text-metrics.ts`) do maior TOKEN indivisível — cabeçalho ou valor —
 *       onde "token" é delimitado por espaço OU hífen, os únicos pontos em
 *       que o pdfmake (algoritmo Unicode UAX#14) realmente quebra linha.
 *       Isso garante que nenhuma PALAVRA da linguagem natural (sem hífen)
 *       seja cortada, e evita superdimensionar colunas por causa de um único
 *       código longo cheio de hífens (ele pode quebrar nos hífens sem isso
 *       ser "cortar uma palavra no meio").
 *       TAG DA LINHA é EXCEÇÃO deliberada: seu mínimo usa o VALOR COMPLETO
 *       (não token), porque a TAG tem prioridade de ficar inteira numa
 *       única linha (comando anterior, item 10; este comando, item 14).
 *  3. cada coluna é classificada em wide/medium/narrow (`LINE_LIST_COLUMN_
 *     PRIORITY`, itens 11/12 do comando) — colunas sem classificação
 *     explícita caem em "medium" (nunca quebram o algoritmo).
 *  4. o espaço que sobra além da soma dos mínimos é distribuído
 *     proporcionalmente ao peso de prioridade — larguras finais = mínimo +
 *     fatia proporcional do excedente.
 *  5. quebra de linha entre palavras é comportamento nativo do pdfmake
 *     (nada a fazer aqui: não habilitamos hifenização nem quebra por
 *     caractere).
 *  6–7. padding fixo compacto (2pt) e margens em dois níveis — tenta a
 *     margem padrão (mais confortável) e só reduz para a mínima aceitável
 *     se a soma dos mínimos não couber, antes de desistir. A FONTE NUNCA é
 *     reduzida (comando, itens 9/31: sem compressão horizontal, sem
 *     deformar caracteres — regra já vigente e não regredida).
 *  8. (fonte) — deliberadamente não ajustada; ver acima.
 *  9. altura de linha: automática pelo próprio pdfmake ao quebrar texto em
 *     múltiplas linhas (nada a fazer aqui).
 *  10. validação final: a soma das larguras calculadas nunca excede a
 *      largura útil escolhida — se por algum defeito de implementação isso
 *      ocorrer, é um erro de invariante (`throw`), não um PDF corrompido
 *      silenciosamente.
 *
 * Se, mesmo no nível de margem mais compacto, a soma das larguras MÍNIMAS
 * (sem nenhuma folga) ainda ultrapassar a largura útil da página, o
 * algoritmo retorna falha explícita (comando, item 21) em vez de gerar um
 * PDF ilegível — quem usa este módulo deve tratar `ok:false` renderizando o
 * aviso técnico, nunca a tabela.
 */

export type LineListColumnPriority = "wide" | "medium" | "narrow";

const PRIORITY_WEIGHT: Record<LineListColumnPriority, number> = { wide: 3, medium: 2, narrow: 1 };

/** Classificação de prioridade de largura (comando, itens 11/12). */
const LINE_LIST_COLUMN_PRIORITY: Partial<Record<keyof GeneratedLineListEntry, LineListColumnPriority>> = {
  tag: "wide",
  fluid: "wide",
  service: "wide",
  from: "wide",
  to: "wide",
  drawingReference: "wide",
  flowDiagramReference: "wide",
  schedule: "narrow",
  spec: "narrow",
  fluidClass: "narrow",
  nr13Category: "narrow",
  designPressure: "narrow",
  operatingPressure: "narrow",
  designTemperature: "narrow",
  operatingTemperature: "narrow",
  mawp: "narrow",
  pressureClass: "narrow"
};

function priorityOf(key: keyof GeneratedLineListEntry): LineListColumnPriority {
  return LINE_LIST_COLUMN_PRIORITY[key] ?? "medium";
}

/** Margens laterais candidatas (pt): tenta a mais confortável primeiro (8mm), reduz para 5mm só se necessário. Nunca reduz a fonte. */
export const LINE_LIST_SIDE_MARGIN_TIERS_PT: readonly number[] = [22.68, 14.17];

/** Padding real da célula (`line-list-render.ts`, `paddingLeft`/`paddingRight`) — a medição usa a métrica real da mesma fonte Roboto embutida no pdfmake, sem necessidade de folga extra. */
// Compact 0.5pt per side keeps the full official table (including PSV and
// INSTRUMENTOS) within A3 landscape while preserving the table's outer gutter.
const CELL_HORIZONTAL_BUDGET_PT = 0.5 + 0.5;

export interface LineListColumnSpec {
  key: keyof GeneratedLineListEntry;
  label: string;
}

export interface LineListLayoutSuccess {
  ok: true;
  widths: number[];
  sideMarginPt: number;
  fontSize: number;
}

export interface LineListLayoutFailure {
  ok: false;
  message: string;
  offendingColumns: { key: string; label: string; requiredPt: number }[];
  shortfallPt: number;
}

export type LineListLayoutResult = LineListLayoutSuccess | LineListLayoutFailure;

function columnMinWidthPt(column: LineListColumnSpec, lines: readonly GeneratedLineListEntry[], fontSize: number): number {
  const headerWidth = measureLongestTokenWidth(column.label, { bold: true, fontSize });
  if (column.key === "tag") {
    const longestFullTag = lines.reduce((max, line) => Math.max(max, measureTextWidth(String(line.tag ?? ""), { fontSize })), 0);
    return Math.max(headerWidth, longestFullTag) + CELL_HORIZONTAL_BUDGET_PT;
  }
  const longestValueToken = lines.reduce((max, line) => Math.max(max, measureLongestTokenWidth(String(line[column.key] ?? ""), { fontSize })), 0);
  return Math.max(headerWidth, longestValueToken) + CELL_HORIZONTAL_BUDGET_PT;
}

/**
 * Calcula as larguras (pt) das colunas da Lista de Linhas para caber em uma
 * única página de largura `pageWidthPt`, a partir das colunas e dados reais.
 */
export function computeLineListLayout(
  columns: readonly LineListColumnSpec[],
  lines: readonly GeneratedLineListEntry[],
  pageWidthPt: number,
  fontSize: number
): LineListLayoutResult {
  const minWidths = columns.map(column => columnMinWidthPt(column, lines, fontSize));
  const weights = columns.map(column => PRIORITY_WEIGHT[priorityOf(column.key)]);
  const sumMin = minWidths.reduce((a, b) => a + b, 0);

  let chosenMargin: number | null = null;
  let contentWidth = 0;
  for (const margin of LINE_LIST_SIDE_MARGIN_TIERS_PT) {
    const candidateWidth = pageWidthPt - margin * 2;
    if (sumMin <= candidateWidth) {
      chosenMargin = margin;
      contentWidth = candidateWidth;
      break;
    }
  }

  if (chosenMargin === null) {
    const mostGenerousWidth = pageWidthPt - Math.min(...LINE_LIST_SIDE_MARGIN_TIERS_PT) * 2;
    const offendingColumns = columns
      .map((column, index) => ({ key: String(column.key), label: column.label, requiredPt: Math.round(minWidths[index]! * 100) / 100 }))
      .sort((a, b) => b.requiredPt - a.requiredPt)
      .slice(0, 6);
    return {
      ok: false,
      message: "Não foi possível gerar a Lista de Linhas mantendo os critérios mínimos de legibilidade e integridade dos dados.",
      offendingColumns,
      shortfallPt: Math.round((sumMin - mostGenerousWidth) * 100) / 100
    };
  }

  const surplus = contentWidth - sumMin;
  const totalWeight = weights.reduce((a, b) => a + b, 0);
  const widths = minWidths.map((minWidth, index) => minWidth + (totalWeight > 0 ? (surplus * weights[index]!) / totalWeight : 0));

  const totalWidth = widths.reduce((a, b) => a + b, 0);
  if (totalWidth > contentWidth + 0.5) {
    throw new Error(`Falha de invariante no autoajuste da Lista de Linhas: largura total (${totalWidth}pt) excede a largura útil (${contentWidth}pt).`);
  }

  return {
    ok: true,
    widths: widths.map(width => Math.round(width * 100) / 100),
    sideMarginPt: chosenMargin,
    fontSize
  };
}
